import { URLParameters } from '@/app/common/urlParameters.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { PAGE_TYPE } from '@/app/common/pageType.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';

const isFilterName = (name: string) => Object.values(URLParameters).find((paramName) => paramName === name);

export const removeAllFilters = (searchParams: SearchParams): SearchParams =>
  Object.entries(searchParams).reduce((result, [key, value]) => {
    const param = isFilterName(key) ? {} : { [key]: value };
    return { ...result, ...param };
  }, {});

export const getSearchParam = (searchParams: SearchParams, paramName: URLParameters): string =>
  searchParams?.[paramName] || '';

export const removeParameters = (searchParams: SearchParams, paramsToRemove: URLParameters[]): SearchParams =>
  Object.entries(searchParams).reduce((result, [key, value]) => {
    const keyToCheck = key as unknown as URLParameters;
    const param = paramsToRemove.includes(keyToCheck) ? {} : { [key]: value };
    return { ...result, ...param };
  }, {});

export const getPageTypeFromString = (pageTypeString: string): PAGE_TYPE =>
  (Object.values(PAGE_TYPE) as string[]).includes(pageTypeString) ? (pageTypeString as PAGE_TYPE) : PAGE_TYPE.SEARCH;

export const getPageTypeFromSearchParams = (searchParams: SearchParams): PAGE_TYPE =>
  getPageTypeFromString(searchParams[URLParameters.PAGE_TYPE] || '');

export const getSearchParamsFromQueryString = (queryString: string | undefined): SearchParams => {
  if (!queryString) return {} as SearchParams;
  const searchParams: SearchParams = {};
  const params = new URLSearchParams(queryString);
  params.forEach((value, key) => {
    searchParams[key] = value;
  });
  return searchParams;
};

export function updateUrl({
  queryParamsToSet,
  queryParamsToRemove,
}: {
  queryParamsToSet?: { [key: string]: string }[];
  queryParamsToRemove?: string[];
  debug?: boolean;
}) {
  if (!canUseDOM) {
    return;
  }

  const url = new URL(window.location.href);
  let shouldUpdateUrl = false;

  if (queryParamsToSet) {
    queryParamsToSet.forEach((prop) => {
      const entries = Object.entries(prop);
      if (entries.length !== 0) {
        const [paramName, paramValue] = entries[0];

        if (url.searchParams.get(paramName) !== paramValue) {
          url.searchParams.set(paramName, typeof paramValue === 'object' ? JSON.stringify(paramValue) : paramValue);
          shouldUpdateUrl = true;
        }
      }
    });
  }

  if (queryParamsToRemove) {
    queryParamsToRemove.forEach((paramName) => {
      if (url.searchParams.get(paramName)) {
        url.searchParams.delete(paramName);
        shouldUpdateUrl = true;
      }
    });
  }

  if (shouldUpdateUrl) {
    // IMPORTANT: pass the existing history state instead of {} to avoid wiping Next.js's internal
    // router state (__NA marker and navigation tree). Passing {} causes Next.js to lose track of this
    // history entry on subsequent popstate events, treating it as an external navigation and
    // triggering a full page re-render. Preserving the state keeps Next.js's router in sync
    // without needing a router.replace() call to re-sync it.
    window.history.replaceState(window.history.state, document.title, url);
  }
}
